import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { defaultSnapProps, snapPoints as snapPointsType } from 'react-spring-bottom-sheet/dist/types';
import { useElementSize } from 'usehooks-ts';

import { Button, SvgIconUrl, Tooltip } from 'common/components';
import { TooltipTriggerType } from 'common/components/Tooltip/Tooltip.component';
import { AlignType } from 'common/components/Tooltip/types';

import { useBowser, useI18n } from 'common/hooks';
import { SvgProps } from 'common/svgIcons';

import { memoizedComponent } from 'utils/memoizeComponent';

import { DropdownCheckbox } from './DropdownCheckbox';
import { DropdownSelect } from './DropdownSelect';
import { DropdownSwitcher } from './DropdownSwitcher';
import { DropdownTab } from './DropdownTabs';
import { getDropdownClassName, getIconClassNames, getLabelClassNames, getWrapperClassNames } from './classNames';
import './index.css';

export type DropdownOptionValue = string | number | undefined | null;

export interface DropdownOption<T = DropdownOptionValue> {
  id?: T;
  value: T;
  'data-test'?: string;
  description?: string;
  label: string | React.ReactNode;
  icon?: SvgProps | string;
  type?: 'select' | 'checkbox' | 'switcher' | 'tabs';
  warn?: boolean;
  onRemove?: (option: T) => void;
  onHide?: (option: T) => void;
  options?: DropdownOption<T>[];
  hidden?: boolean;
  checked?: boolean;
  onSelect?: (option: DropdownOption<T>, innerSelectedOption?: string) => void;
}

interface DropdownProps<T> {
  openedInitially?: boolean;
  options: DropdownOption<T>[];
  onSelect?: (option: DropdownOption<T>, innerSelectedOption?: string) => void;
  onClose?: () => void;
  onOpen?: () => void;
  className?: string;
  overlayClassName?: string;
  icon?: SvgProps;
  label?: ((isOpen: boolean) => string | React.ReactNode) | string | React.ReactNode;
  tooltip?: string;
  offset?: number | number[];
  value?: DropdownOptionValue | DropdownOptionValue[];
  'data-test'?: string;
  placement?: 'left' | 'right' | 'top' | 'bottom' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  children?: ((isOpen: boolean) => React.ReactNode) | React.ReactNode;
  title?: string | React.ReactNode;
  isSideSheet?: boolean;
  disabled?: boolean;
  keepOpen?: boolean;
  minWidth?: (params: { contentWidth: number; defaultWidth: number }) => number | undefined;
  useDefinedSnapPoints?: boolean;
  searchTerm?: string;
  searchField?: React.ReactNode;
  searchNotFound?: React.ReactNode;
  keepOnScroll?: boolean;
}

export const DropdownComponent = <T extends DropdownOptionValue>({
  openedInitially,
  options,
  icon,
  onSelect,
  onClose,
  onOpen,
  value,
  label,
  tooltip = '',
  className = '',
  overlayClassName = '',
  disabled,
  placement = 'right',
  offset,
  isSideSheet,
  title,
  minWidth,
  useDefinedSnapPoints = true,
  searchField,
  searchNotFound,
  searchTerm,
  keepOnScroll,
  ...props
}: DropdownProps<T>) => {
  const { isMobile } = useBowser();
  const { translate } = useI18n();
  const [ref, { width }] = useElementSize<HTMLSpanElement>();

  const [enabled, toggle] = useState(openedInitially ?? true);
  const [isOpen, setOpen] = useState(openedInitially ?? false);

  const close = useCallback(() => setOpen(false), []);

  const enable = useCallback(() => toggle(true), []);
  const disable = useCallback(() => toggle(false), []);

  const onVisabilityChanged = useCallback((isVisible = false) => setOpen(isVisible), []);

  const onCloseButtonClick = useCallback(() => {
    close();
    onClose?.();
  }, [close, onClose]);

  const onClick = useCallback(
    (option: DropdownOption<T>, innerSelectedOption?: string) => {
      if (option.onSelect) option.onSelect(option, innerSelectedOption);
      else onSelect?.(option, innerSelectedOption);

      if (props.keepOpen) return;

      disable();
      close();
      onClose?.();
    },
    [onSelect, props.keepOpen, disable, close, onClose],
  );

  useEffect(() => {
    if (openedInitially) return setOpen(true);
  }, [openedInitially]);

  useEffect(() => {
    if (isOpen === false) onClose?.();
    if (isOpen === true) onOpen?.();
  }, [isOpen, onOpen, onClose]);

  const align = useMemo<AlignType | undefined>(() => {
    if (Array.isArray(offset)) return { targetOffset: offset };

    switch (placement) {
      case 'top':
      case 'topLeft':
      case 'topRight':
        return { targetOffset: [0, offset || 8] };
      case 'bottom':
      case 'bottomLeft':
      case 'bottomRight':
        return { targetOffset: [0, offset || -8] };

      default:
        return undefined;
    }
  }, [offset, placement]);

  const renderOption = useCallback(
    (option: DropdownOption<T>) => {
      const isActive = Array.isArray(value) ? value.includes(option.value) : option.value === value;

      const handleCommonOptionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();
        onClick({ ...option, checked: !option.checked });
      };

      const handleTabsOptionClick = (tabId?: string) => {
        const { value: targetInnerValue, onSelect: targetInnerOnSelect } =
          option?.options?.find((o) => o.value === tabId) ?? {};
        if (targetInnerValue) {
          onClick(option, targetInnerValue.toString());
        }
        if (targetInnerOnSelect) targetInnerOnSelect(option, String(targetInnerValue));
      };

      switch (option.type) {
        case 'tabs':
          return <DropdownTab key={option.value} option={option} isActive={isActive} onClick={handleTabsOptionClick} />;
        case 'checkbox':
          return (
            <DropdownCheckbox
              key={option.value}
              option={option}
              isActive={isActive || option.checked}
              onClick={handleCommonOptionClick}
            />
          );
        case 'switcher':
          return (
            <DropdownSwitcher key={option.value} option={option} isActive={isActive} onClick={handleTabsOptionClick} />
          );
        case 'select':
        default:
          return (
            <DropdownSelect
              key={option.value}
              option={option}
              isActive={isActive}
              onClick={handleCommonOptionClick}
              className={className}
              searchTerm={searchTerm}
            />
          );
      }
    },
    [className, onClick, searchTerm, value],
  );

  const Options = useMemo(
    () => (
      <>
        {searchField ?? null}
        {searchNotFound && !options.length ? searchNotFound : null}
        {options.map((option) => renderOption(option))}
      </>
    ),
    [searchField, searchNotFound, options, renderOption],
  );

  const wrapperClassNames = useMemo(
    () => getWrapperClassNames(className, isOpen, disabled),
    [className, disabled, isOpen],
  );
  const labelClassNames = useMemo(() => getLabelClassNames(className, isOpen), [className, isOpen]);
  const iconClassNames = useMemo(() => getIconClassNames(className, isOpen), [className, isOpen]);

  const renderLabel = useCallback(() => {
    switch (typeof label) {
      case 'function':
        return label(isOpen);

      default:
        return label;
    }
  }, [isOpen, label]);

  const renderChildren = useCallback(() => {
    switch (typeof props.children) {
      case 'function':
        return props.children(isOpen);

      default:
        return props.children;
    }
  }, [isOpen, props]);

  const valueRef = React.useRef<HTMLSpanElement>(document.body);

  const onClickValueHandler = useCallback<React.MouseEventHandler<HTMLDivElement>>((e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  }, []);

  const Value = useMemo(
    () => (
      <span
        ref={valueRef}
        onMouseOver={enable}
        onClick={onClickValueHandler}
        className={wrapperClassNames}
        data-test={props['data-test']}
        data-value={value}
      >
        {props.children && (
          <span ref={ref} className={labelClassNames}>
            {renderChildren()}
          </span>
        )}
        {label && (
          <span data-test={`${props['data-test']}-label`} className={labelClassNames}>
            {renderLabel()}
          </span>
        )}
        {icon && <SvgIconUrl data-test={`${props['data-test']}-icon`} className={iconClassNames} symbol={icon} />}
      </span>
    ),
    [
      value,
      ref,
      enable,
      icon,
      iconClassNames,
      label,
      labelClassNames,
      onClickValueHandler,
      props,
      renderChildren,
      renderLabel,
      wrapperClassNames,
    ],
  );

  const dropdownClassName = useMemo(
    () => getDropdownClassName(overlayClassName, disabled),
    [disabled, overlayClassName],
  );

  const dropdownSheetClassName = useMemo(
    () => classNames(dropdownClassName, { 'dropdown--sheet': true }),
    [dropdownClassName],
  );

  const header = useMemo(() => !!title && <h5 className='dropdown__title'>{title}</h5>, [title]);

  const _minWidth = useMemo(() => {
    if (minWidth) return minWidth({ contentWidth: width, defaultWidth: 300 });

    return undefined;
  }, [minWidth, width]);

  const snapPointsCallback = useCallback<snapPointsType>(({ maxHeight, minHeight }) => [maxHeight - 35, minHeight], []);
  const defaultSnapPointsCallback = useCallback(({ snapPoints }: defaultSnapProps) => Math.min(...snapPoints), []);
  const snapPoints = useMemo(
    () => (useDefinedSnapPoints ? snapPointsCallback : undefined),
    [useDefinedSnapPoints, snapPointsCallback],
  );

  const clickTrigger: TooltipTriggerType = useMemo(() => ['click'], []);
  const hoverTrigger: TooltipTriggerType = useMemo(() => ['hover'], []);

  if (isSideSheet)
    return (
      <>
        {header}
        <div className={dropdownSheetClassName}>{Options}</div>
        {props.keepOpen && (
          <Button onClick={onCloseButtonClick} stretch large className='dropdown__button'>
            {translate.toText('mobile:dropdown:done')}
          </Button>
        )}
      </>
    );

  if (isMobile) {
    return (
      <>
        {Value}
        <BottomSheet
          className={`light dropdown__sheet ${className}__sheet`}
          initialFocusRef={false}
          expandOnContentDrag
          onDismiss={close}
          blocking
          scrollLocking
          snapPoints={snapPoints}
          defaultSnap={defaultSnapPointsCallback}
          open={isOpen}
          header={header}
          footer={
            props.keepOpen && (
              <div className='dropdown__sheet-bottom'>
                <Button onClick={close} stretch large className='dropdown__button'>
                  {translate.toText('mobile:dropdown:done')}
                </Button>
              </div>
            )
          }
        >
          <div className={dropdownClassName}>{Options}</div>
        </BottomSheet>
      </>
    );
  }

  return (
    <Tooltip
      placement={placement}
      trigger={clickTrigger}
      overlayClassName={dropdownClassName}
      overlay={Options}
      enabled={enabled}
      afterVisibleChange={onVisabilityChanged}
      overlayStyle={{ minWidth: _minWidth }}
      align={align}
      getTooltipContainer={() => (keepOnScroll ? valueRef.current : document.body)}
    >
      <Tooltip
        overlayStyle={{ zIndex: 9999 }}
        placement={placement}
        trigger={hoverTrigger}
        overlayClassName='light'
        overlay={tooltip}
        enabled={!!tooltip && !isOpen}
      >
        {Value}
      </Tooltip>
    </Tooltip>
  );
};

export const Dropdown = memoizedComponent(DropdownComponent);
